/**
 * Translations for menus, breadcrumbs, and UI labels
 * Supports French (fr) and English (en)
 */

import {
  getCurrentLanguage,
  Language,
} from '@fastlessjs/backoffice-layer/dist/hooks/useLanguage';
import {
  registerBreadcrumbTranslations,
  registerHomeTranslations,
} from '@fastlessjs/backoffice-layer/dist/utils/translations';

export type { Language };

/**
 * Pick guard / rule block text for the current UI language.
 * Prefer user's locale, then the other locale if missing, then `fallback` (`onFail.message`).
 */
export function pickRuleUserMessage(
  fallback: string,
  messages?: { fr?: string; en?: string },
): string {
  const lang = getCurrentLanguage();
  const pick = (s?: string) => {
    const t = String(s ?? '').trim();
    return t.length ? t : undefined;
  };
  if (!messages) return fallback;
  const primary = lang === 'en' ? messages.en : messages.fr;
  const secondary = lang === 'en' ? messages.fr : messages.en;
  return pick(primary) ?? pick(secondary) ?? fallback;
}

/**
 * Breadcrumb translations - registered with the layer for URL path translation
 */
const breadcrumbTranslations = {
  fr: {
    returns: 'Retour',
    suivi: 'Suivis',
    orders: 'Commandes',
    enterprises: 'Entreprises',
    users: 'Utilisateurs',
    groups: 'Groupes',
    policies: 'Politiques',
    iam: 'Gestion des accès',
    trace: 'Suivi',
    products: 'Produits',
  },
  en: {
    returns: 'Returns',
    suivi: 'Tracking',
    orders: 'Orders',
    enterprises: 'Enterprises',
    users: 'Users',
    groups: 'Groups',
    policies: 'Policies',
    iam: 'Access Management',
    trace: 'Tracking',
    products: 'Products',
  },
};

/**
 * Home page translations for welcome message
 */
const homeTranslations = {
  fr: {
    welcomeSubtitle: 'Prêt à gérer vos retours ?',
  },
  en: {
    welcomeSubtitle: 'Ready to manage your returns?',
  },
};

/**
 * General translations for menus, descriptions, and UI labels
 */
export const translations = {
  fr: {
    // Menu labels
    'menu.returns': 'Retour',
    'menu.suivi': 'Suivis',
    'menu.orders': 'Commandes',
    'menu.enterprises': 'Entreprises',
    'menu.iam': 'Gestion des accès',
    'menu.users': 'Utilisateurs',
    'menu.groups': 'Groupes',
    'menu.policies': 'Politiques',

    'menu.feedbacks': 'Feedback',
    'menu.feedbacks.description':
      'Signaler une recommandation incorrecte pour analyse superadmin.',

    // Feedback
    'feedback.title': 'Feedback',
    'feedback.feedbackType': 'Type de feedback',
    'feedback.type.sizes': 'Tailles',
    'feedback.listDescription': 'Feedbacks pour l’organisation sélectionnée.',
    'feedback.formDescription':
      'Décrivez l’entreprise, le trousseau, la personne ou la morphologie, et les produits concernés.',
    'feedback.viewTitle': 'Détail du feedback',
    'feedback.createTitle': 'Nouveau feedback',
    'feedback.editTitle': 'Modifier le feedback',
    'feedback.create': 'Nouveau feedback',
    'feedback.edit': 'Modifier',
    'feedback.view': 'Voir',
    'feedback.save': 'Enregistrer',
    'feedback.formValidationError':
      'Vérifie les champs obligatoires (répondant, produits, tailles).',
    'feedback.unknownField': 'Champ non autorisé',
    'feedback.backToList': 'Retour à la liste',
    'feedback.created': 'Feedback déposé',
    'feedback.updated': 'Feedback mis à jour',
    'feedback.statusUpdated': 'Statut mis à jour',
    'feedback.filterStatus': 'Filtrer par statut',
    'feedback.currentStatus': 'Statut actuel',
    'feedback.createdAt': 'Créé le',
    'feedback.columnCreated': 'Créé',
    'feedback.columnUpdated': 'Mis à jour',
    'feedback.enterpriseId': 'Entreprise',
    'feedback.trousseauId': 'Trousseau',
    'feedback.personId': 'Répondant',
    'feedback.subjectMode': 'Identification',
    'feedback.subjectPerson': 'Répondant',
    'feedback.subjectMorphology': 'Morphologie',
    'feedback.height': 'Taille (cm)',
    'feedback.weight': 'Poids (kg)',
    'feedback.age': 'Âge (ans)',
    'feedback.neckGirth': 'Tour de cou (cm)',
    'feedback.headGirth': 'Tour de tête (cm)',
    'feedback.chestGirth': 'Tour de poitrine (cm)',
    'feedback.hipGirth': 'Tour de hanches (cm)',
    'feedback.products': 'Produits',
    'feedback.productId': 'Produit',
    'feedback.recommendedSize': 'Taille recommandée',
    'feedback.desiredSize': 'Taille souhaitée',
    'feedback.addProduct': 'Ajouter un produit',
    'feedback.removeProduct': 'Supprimer le produit',
    'feedback.status': 'Statut',
    'feedback.sortByDate': 'Par date',
    'feedback.sortByName': 'Par nom',
    'feedback.feedbackId': 'Identifiant',
    'feedback.enterpriseIdRequired': 'Enterprise est requis',
    'feedback.trousseauIdRequired': 'Trousseau est requis',
    'feedback.personIdRequired': 'Répondant est requis',
    'feedback.heightRequired': 'Taille est requise',
    'feedback.weightRequired': 'Poids est requis',
    'feedback.ageRequired': 'Âge est requis',
    'feedback.productIdRequired': 'Produit est requis',
    'feedback.recommendedSizeRequired': 'Taille recommandée est requise',
    'feedback.desiredSizeRequired': 'Taille souhaitée est requise',
    'feedback.status.deposited': 'Déposé',
    'feedback.status.in_progress': 'En cours',
    'feedback.status.processed': 'Traité',
    'feedback.tabSubject': 'Personne / morphologie',
    'feedback.tabSubjectDescription':
      'Renseignez le répondant ou la morphologie (l’un des deux est requis).',
    'feedback.productsFormDescription':
      'Ajoutez une ligne par produit avec la taille recommandée et celle souhaitée.',
    'feedback.scopeTitle': 'Organisation cible',
    'feedback.scopeDescription':
      'Les feedbacks sont enregistrés dans la base de l’organisation sélectionnée.',
    'feedback.processedAt': 'Traité le',
    'feedback.processedBy': 'Traité par',

    // Menu descriptions
    'menu.returns.description':
      "Demander un retour et gérer les demandes de retour qui n'ont pas été envoyées.",
    'menu.suivi.description':
      'Consulter le suivi des demandes de retour et les retours effectués.',
    'menu.orders.description': "Consulter l'historique des commandes",
    'menu.enterprises.description': 'Manager les entreprises présentes sur la plateforme',
    'menu.iam.description':
      'Manager les utilisateurs, leurs groupes et leurs règles de gestion',

    // Notifications
    'notification.return.error': 'Votre demande de retour a échoué.',
    'notification.return.error.duplicate':
      'Une demande de retour existe déjà pour cette commande.',
    'notification.return.success.title': 'Votre demande de retour est envoyée.',

    // OrderNotFound
    'notFound.clientNotFound': 'Client introuvable',
    'notFound.orderNotFound': 'Commande introuvable',
    'notFound.noResults': 'Recherche sans résultat',
    'notFound.noClientFound': 'Aucun client trouvé avec le numéro',
    'notFound.noOrderFound': 'Aucune commande trouvée avec le numéro',
    'notFound.forClient': 'pour le client',
    'notFound.noResultsMessage':
      "Nous n'avons pas trouvé de résultat correspondant à votre recherche",
    'notFound.checkClientNumber': 'Vérifiez votre numéro de client',
    'notFound.clientNumberLocation':
      'Le numéro de client se trouve en haut à gauche de votre facture (ex: 123456)',
    'notFound.checkOrderNumber': 'Vérifiez votre numéro de commande',
    'notFound.orderNumberLocation':
      'Le numéro de commande se trouve dans la colonne "Concerne commande" de votre facture (8 chiffres)',
    'notFound.checkInvoice': 'Consultez votre facture',
    'notFound.invoiceInfo':
      'Munissez-vous de votre facture pour retrouver facilement les informations nécessaires',
    'notFound.searchedInfo': 'Informations recherchées :',
    'notFound.client': 'Client :',
    'notFound.order': 'Commande :',
    'notFound.newSearch': 'Nouvelle recherche',
    'notFound.suggestions': 'Suggestions pour retrouver vos informations',

    // SearchOrder
    'searchOrder.clientNumber': 'Numéro de client',
    'searchOrder.clientNumberHelp':
      'Saisissez votre numéro de client (ex: 123456 ou 0123456)',
    'searchOrder.orderNumber': 'Numéro de commande',
    'searchOrder.orderNumberHelp': 'Saisissez votre numéro de commande (8 chiffres)',
    'searchOrder.whereToFind': 'Où trouver vos numéros ?',
    'searchOrder.clientNumberExample': 'Ex :',
    'searchOrder.topLeftInvoice': 'Dans la colonne "Client N°" de votre facture',
    'searchOrder.orderColumn': 'Colonne "Concerne commande"',
    'searchOrder.understood': "J'ai compris",
    'searchOrder.searchTitle': 'Recherchez votre commande',
    'searchOrder.searchSubtitle':
      'Suivez les étapes pour retrouver vos informations de retour',
    'searchOrder.progress': 'Progression',
    'searchOrder.steps': 'étapes',
    'searchOrder.step1Conditions': 'Étape 1: Conditions de retour',
    'searchOrder.step2Client': 'Étape 2: Identification du client',
    'searchOrder.step3Order': 'Étape 3: Recherche de commande',
    'searchOrder.conditionsIntro':
      'Avant de commencer votre demande de retour, veuillez prendre connaissance de nos conditions :',
    'searchOrder.itemState': 'État des articles',
    'searchOrder.itemStateContent':
      "Articles retournés neufs, dans leur sachet d'origine, non lavés, non transformés, non souillés et non portés.",
    'searchOrder.returnDelay': 'Délai de retour',
    'searchOrder.returnDelayContent':
      "30 jours ouvrables à partir de la livraison. Le bordereau d'expédition fait foi.",
    'searchOrder.excludedProducts': 'Produits exclus',
    'searchOrder.excludedProductsContent':
      'Produits personnalisés, brodés, modifiés et en fin de série non acceptés.',
    'searchOrder.refund': 'Remboursement',
    'searchOrder.refundContent':
      'Joindre impérativement votre RIB pour tout remboursement.',
    'searchOrder.acceptConditions': "J'ai lu et j'accepte les conditions",
    'searchOrder.conditionsAccepted': 'Conditions acceptées',
    'searchOrder.enterClientId':
      'Saisissez votre numéro de client pour accéder à vos commandes.',
    'searchOrder.backToConditions': 'Retour aux conditions',
    'searchOrder.validateAndContinue': 'Valider et continuer',
    'searchOrder.invalidClientFormat': 'Format de numéro client invalide',
    'searchOrder.acceptConditionsFirst': "Acceptez d'abord les conditions de retour",
    'searchOrder.modify': 'Modifier',
    'searchOrder.enterOrderId':
      'Maintenant, saisissez le numéro de votre commande pour accéder aux détails et options de retour.',
    'searchOrder.backToClient': "Retour à l'identification client",
    'searchOrder.searchOrder': 'Rechercher la commande',
    'searchOrder.completeClientFirst': "Complétez d'abord l'identification du client",
    'searchOrder.orderNumberToEnter': 'Numéro de commande à saisir',
    'notification.return.success.description':
      'Nous vous remercions pour votre retour, vous allez recevoir un email de confirmation avec les modalités de renvoi de votre ou vos article(s).',
    'notification.return.pending.description':
      'Votre demande est en cours de traitement. La confirmation sera mise à jour automatiquement.',
    'notification.return.pending.stillProcessing':
      'Le suivi n’est pas encore confirmé. Vérifiez l’état dans Suivi plus tard.',

    // Button titles
    'button.returnItem': 'Retourner cet article',
    'button.deleteReturn': 'Supprimer ce retour',
    'button.editReturn': 'Éditer ce retour',
    'button.submitReturn': 'Soumettre ce retour',
    'button.validateReturn': 'Valider le retour',
    'button.myReturnRequests': 'Mes demandes de retour',
    'button.submitAllReturns': 'Soumettre tous les retours',
    'button.back': 'Retour',

    // Modal titles
    'modal.returnItem': 'Retournez un article',
    'modal.returnRequestsByOrder': 'Vos demandes de retour par commande',

    // List titles
    'list.yourArticle': 'Votre article',
    'list.yourArticles': 'Vos articles',
    'list.addItemsToReturn': 'Ajoutez des articles à retourner',
    'list.products': 'Produits',
    'list.tracking': 'Suivi',

    // Order view
    'order.yourOrderNumber': 'Votre commande numéro',
    'order.of': 'du',
    'order.totalTtc': "d'un total ttc de",
    'order.status': 'état',
    'order.trackingExists': 'Un suivi existe pour cette commande',
    'order.viewTracking': 'Voir le suivi',

    // Return conditions
    'conditions.title': 'Conditions de retour',
    'conditions.itemState.title': 'État des articles',
    'conditions.itemState.content':
      "Les articles doivent être retournés complets dans un état neuf : dans leur sachet d'origine, non lavés, non transformés, non souillés et non portés.",
    'conditions.returnDelay.title': 'Délai de retour',
    'conditions.returnDelay.content':
      "Vous disposez de 30 jours ouvrables à partir de la livraison pour retourner vos articles. Le bordereau d'expédition fait foi. Au-delà, aucun retour ne sera accepté.",
    'conditions.excludedProducts.title': 'Produits exclus',
    'conditions.excludedProducts.content':
      'Les produits personnalisés, brodés, modifiés et en fin de série ne peuvent pas être retournés.',
    'conditions.refund.title': 'Remboursement',
    'conditions.refund.content':
      'Pour un remboursement, joignez impérativement votre RIB. Sans RIB, votre demande ne pourra pas être traitée.',

    // Return form
    'form.chooseQuantity': 'Choisissez la quantité à retourner',
    'form.quantityReturned': 'Quantité retournée',
    'form.returnType': 'Type de retour',
    'form.returnReason': 'Motif de retour',
    'form.newSizeRequested': 'Nouvelle taille demandée',
    'form.credit': 'Avoir',
    'form.exchange': 'Échange',
    'form.refund': 'Remboursement',
    'form.exchangeUnavailable': 'Échange indisponible pour cause de tailles épuisées.',
    'form.ribWarning': 'Attention, veuillez fournir un RIB avec votre bon de retour.',

    // Form errors
    'error.quantityRequired': 'Veuillez entrer une quantité à retourner',
    'error.returnTypeRequired': 'Veuillez sélectionner un type de retour',
    'error.returnReasonRequired': 'Veuillez choisir un motif de retour',
    'error.quantityMin': 'La quantité minimum retournée est de 1 article',
    'error.sizeRequired': 'Veuillez sélectionner une nouvelle taille demandée',

    // Misc
    'misc.itemNotReturnable': 'Article non retournable',
    'misc.loading': 'Chargement...',
    'misc.client': 'Client',

    // Schema - Column titles
    'schema.orderNumber': 'Numéro de commande',
    'schema.clientReference': 'Référence client',
    'schema.totalAmount': 'Montant total',
    'schema.receptionDate': 'Date de réception',
    'schema.status': 'État',
    'schema.type': 'Type',
    'schema.lines': 'Lignes',
    'schema.designation': 'Désignation',
    'schema.articleNumber': "Numéro d'article",
    'schema.articleRef': 'Réf article',
    'schema.qtyOrdered': 'Qté commandée',
    'schema.qtyToReturn': 'Qté à retourner',
    'schema.qtyReturned': 'Qté retournée',
    'schema.returnType': 'Type de retour',
    'schema.returnReason': 'Motif de retour',
    'schema.requestedSize': 'Taille demandée',
    'schema.replacementArticle': 'Code article de remplacement',
    'schema.unit': 'U',
    'schema.price': 'Prix',
    'schema.total': 'Total',
    'schema.orderRef': 'Commande',
    'schema.isReturnable': 'Article retournable ?',
    'schema.schoolClient': 'Client École',
    'schema.credit': 'Avoir',
    'schema.exchange': 'Échange',
    'schema.refund': 'Remboursement',
    'schema.productTracking': 'Suivi des produits',
    'schema.reference': 'Référence',
    'schema.replacedRef': 'Réf remplacée',
    'schema.size': 'Taille',
    'schema.clientReason': 'Motif client',
    'schema.tracking': 'Suivi',
    'schema.enterpriseId': 'ID entreprise',
    'schema.trackingRef': 'Réf suivi',
    'schema.returnStatus': 'Statut retour',
    'schema.processingStatus': 'État de traitement',
    'schema.trackingDate': 'Date de suivi',
    'trace.returnStatus.pending': 'Demande en cours de traitement',
    'trace.returnStatus.confirmed': 'Retour enregistré',
    'trace.returnStatus.failed': "Échec de l'enregistrement",
    'trace.processingStatus.pending': 'En attente de synchronisation',
    'trace.processingStatus.unavailable': 'Non disponible',
    'schema.name': 'Nom',
    'schema.email': 'Email',
    'schema.creatorEmail': 'Email créateur',
    'schema.creatorRole': 'Rôle créateur',
    'schema.trackingUrl': 'URL de suivi',
    'schema.trackingType': 'Type suivi',
    'schema.returnMotif': 'Motif du retour',
    'schema.userId': 'Identifiant utilisateur',
    'schema.createdBy': 'Créé par',
    'schema.clientEmail': 'Email client',
    'schema.clientName': 'Nom client',
    'schema.createdAt': 'Créé le',
    'schema.orderId': 'ID commande',
    'schema.extOrderId': 'ID externe',
    'schema.enterprise': 'Entreprise',
    'schema.orderDate': 'Date de commande',
    'schema.returnId': 'ID retour',
    'schema.eurekaStatus': 'Statut Eureka',
    'schema.error': 'Erreur',
    'schema.nameNotFound': 'Nom introuvable',

    // Page titles
    'page.orders': 'Commandes',
    'page.order': 'Commande',
    'page.tracking': 'Suivis',

    // ReturnRequestTable
    'list.yourReturnRequest': 'Votre demande de retour',

    // AddReturnForm - schema labels
    'form.creditLabel': 'Avoir',
    'form.exchangeLabel': 'Échange',

    // ReturnDisclaimer modal
    'modal.attention': 'Attention !',
    'modal.optimizeChoice': 'Optimisez votre choix avant de retourner vos articles',
    'button.cancel': 'Annuler',

    // SearchOrderModal
    'searchModal.searchOrder': 'Rechercher une commande',
    'searchModal.searchAnOrder': 'Rechercher une commande',
    'searchModal.openDetails': 'Voir les détails',
    'searchModal.search': 'Rechercher',
    'searchModal.enterOrderNumber': 'Entrez le numéro de commande :',
    'searchModal.orderPlaceholder': 'Numéro de commande Ex: 123456',
    'searchModal.searchReturn': 'Rechercher un retour',
    'searchModal.searchAReturn': 'Rechercher un retour',
    'searchModal.enterOrderNumberForReturn':
      'Entrez le numéro de commande pour retrouver le retour associé :',
    'searchModal.multipleReturnsFound':
      '{{count}} retours trouvés pour cette commande. Sélectionnez celui à ouvrir.',
    'searchModal.returnNotFound': 'Aucun retour trouvé pour cette commande.',
    'searchModal.missingEnterprise':
      'Entreprise non sélectionnée — impossible de lancer la recherche.',

    // Return Disclaimer - Return reasons
    'disclaimer.returnReason.tooBig': 'trop grande',
    'disclaimer.returnReason.tooSmall': 'trop petite',

    // Return Disclaimer - VESTE CUISIN (Chef Jacket) - Too Big
    'disclaimer.vesteCuisin.grand.sleeveLength.title': 'La longueur des manches',
    'disclaimer.vesteCuisin.grand.sleeveLength.content':
      'Vous avez la possibilité de retourner (ou retrousser) les manches, les vestes sont adaptées.',
    'disclaimer.vesteCuisin.grand.jacketTooLong.title': 'La veste est trop longue',
    'disclaimer.vesteCuisin.grand.jacketTooLong.content':
      'Si la veste vous convient au niveau des épaules, il ne faut pas la changer, elle serait trop petite sans liberté de mouvements.',
    'disclaimer.vesteCuisin.grand.jacketTooWide.title':
      'La veste est trop large au niveau du torse',
    'disclaimer.vesteCuisin.grand.jacketTooWide.content':
      "Vérifier si c'est une veste de taille mixte mais que les épaules conviennent et donc la taille est bonne : c'est la forme de la veste qui est droite donc pas cintrée.",

    // Return Disclaimer - VESTE CUISIN (Chef Jacket) - Too Small
    'disclaimer.vesteCuisin.petit.sleeveLength.title': 'La longueur des manches',
    'disclaimer.vesteCuisin.petit.sleeveLength.content':
      'Si la taille des épaules convient, il ne faut pas la changer, elle serait trop grande, beaucoup moins élégante !',
    'disclaimer.vesteCuisin.petit.jacketTooShort.title':
      'La veste est trop petite dans la longueur',
    'disclaimer.vesteCuisin.petit.jacketTooShort.content':
      'Si la veste vous convient au niveau des épaules, il ne faut pas la changer, elle serait trop grande, beaucoup moins élégante !',
    'disclaimer.vesteCuisin.petit.jacketTooTightHips.title':
      'La veste est trop petite au niveau des hanches (surtout les femmes)',
    'disclaimer.vesteCuisin.petit.jacketTooTightHips.content':
      "Si la taille des épaules convient, la taille est bonne. C'est la forme de la veste qui est cintrée afin de souligner vos formes féminines. Si c'est juste le bouton du bas qui ne se ferme pas, votre tablier masquera le bouton ouvert et seulement le haut de la veste sera visible.",

    // Return Disclaimer - VESTE COSTU (Suit Jacket) - Too Big
    'disclaimer.vesteCostu.grand.sleeveLength.title': 'La longueur des manches',
    'disclaimer.vesteCostu.grand.sleeveLength.content':
      'Vous avez la possibilité de retourner (ou retrousser) les manches, les vestes sont adaptées.',
    'disclaimer.vesteCostu.grand.jacketTooLong.title': 'La veste est trop longue',
    'disclaimer.vesteCostu.grand.jacketTooLong.content':
      'Si la veste vous convient au niveau des épaules, il ne faut pas la changer, elle serait trop petite sans liberté de mouvements.',
    'disclaimer.vesteCostu.grand.jacketTooWide.title':
      'La veste est trop large au niveau du torse',
    'disclaimer.vesteCostu.grand.jacketTooWide.content':
      "Vérifier si c'est une veste de taille mixte mais que les épaules conviennent et donc la taille est bonne : c'est la forme de la veste qui est droite donc pas cintrée.",

    // Return Disclaimer - VESTE COSTU (Suit Jacket) - Too Small
    'disclaimer.vesteCostu.petit.sleeveLength.title': 'La longueur des manches',
    'disclaimer.vesteCostu.petit.sleeveLength.content':
      'Si la taille des épaules convient, il ne faut pas la changer, elle serait trop grande, beaucoup moins élégante !',
    'disclaimer.vesteCostu.petit.jacketTooShort.title':
      'La veste est trop petite dans la longueur',
    'disclaimer.vesteCostu.petit.jacketTooShort.content':
      'Si la veste vous convient au niveau des épaules, il ne faut pas la changer, elle serait trop grande, beaucoup moins élégante !',
    'disclaimer.vesteCostu.petit.jacketTooTightHips.title':
      'La veste est trop petite au niveau des hanches (surtout les femmes)',
    'disclaimer.vesteCostu.petit.jacketTooTightHips.content':
      "Si la taille des épaules convient, la taille est bonne. C'est la forme de la veste qui est cintrée afin de souligner vos formes féminines. Si c'est juste le bouton du bas qui ne se ferme pas, votre tablier masquera le bouton ouvert et seulement le haut de la veste sera visible.",

    // Return Disclaimer - PANTALON (Pants) - Too Big
    'disclaimer.pantalon.grand.legLength.title': 'Longueur des jambes',
    'disclaimer.pantalon.grand.legLength.content':
      "Vérifier la présence de pressions au niveau du bas du pantalon permettant d'ajuster la longueur. Si le pantalon n'a pas de pression vous pouvez faire réaliser un ourlet auprès d'un couturier de votre choix.",
    'disclaimer.pantalon.grand.legWidth.title': 'Largueur des jambes',
    'disclaimer.pantalon.grand.legWidth.content':
      "C'est le modèle qui est ainsi, la modification de taille ne changera pas la coupe du pantalon.",
    'disclaimer.pantalon.grand.waistSize.title': 'Taille au niveau de la ceinture',
    'disclaimer.pantalon.grand.waistSize.content':
      "Si le pantalon a un cordon de serrage vous pouvez l'utiliser pour ajuster la taille. Si c'est vraiment très grand au niveau de la ceinture « plus de 3 doigts » après resserrage, alors la taille n'est pas adaptée.",

    // Return Disclaimer - PANTALON (Pants) - Too Small
    'disclaimer.pantalon.petit.legLength.title': 'Longueur des jambes',
    'disclaimer.pantalon.petit.legLength.content':
      "Le changement de taille n'allongera pas la longueur des jambes du pantalon. C'est la coupe du pantalon qui est conçue ainsi.",
    'disclaimer.pantalon.petit.thighWidth.title':
      'Largeur des cuisses mais au niveau des tailles cela convient',
    'disclaimer.pantalon.petit.thighWidth.content':
      "N'hésitez pas à prendre 1 à 2 tailles au-dessus. Il faudra ajuster votre pantalon avec une ceinture.",
    'disclaimer.pantalon.petit.waistSize.title': 'Taille au niveau de la ceinture',
    'disclaimer.pantalon.petit.waistSize.content':
      'Prendre 1 à 2 tailles supplémentaires',

    // Return Disclaimer - CHEMISE (Shirt) - Too Big
    'disclaimer.chemise.grand.neckSize.title': 'Au niveau du cou',
    'disclaimer.chemise.grand.neckSize.content':
      'Si vous pouvez passer 2 doigts au niveau du col, la taille est adaptée. Une chemise trop serrée au niveau du cou est très inconfortable.',
    'disclaimer.chemise.grand.sleevesTooLong.title': 'Manches trop longues',
    'disclaimer.chemise.grand.sleevesTooLong.content':
      "Si au niveau du cou cela convient la taille est adaptée. C'est la coupe de la chemise qui est conçue ainsi.",
    'disclaimer.chemise.grand.shirtLength.title': 'Longueur en bas de la chemise',
    'disclaimer.chemise.grand.shirtLength.content':
      "Si au niveau du cou cela convient la taille est adaptée. C'est la coupe de la chemise qui est conçue ainsi.",

    // Return Disclaimer - CHEMISE (Shirt) - Too Small
    'disclaimer.chemise.petit.neckSize.title': 'Au niveau du cou',
    'disclaimer.chemise.petit.neckSize.content':
      'Si vous êtes trop serré au niveau du cou, prendre 1 à 2 tailles plus grandes.',
    'disclaimer.chemise.petit.sleevesTooShort.title': 'Manches trop courtes',
    'disclaimer.chemise.petit.sleevesTooShort.content':
      "Si au niveau du cou cela convient la chemise est adaptée. C'est la coupe de la chemise qui est conçue ainsi.",
    'disclaimer.chemise.petit.shirtLength.title': 'Longueur en bas de la chemise',
    'disclaimer.chemise.petit.shirtLength.content':
      "Si au niveau du cou cela convient la chemise est adaptée. C'est la coupe de la chemise qui est conçue ainsi.",

    // Return Disclaimer - CHAUSSURES (Shoes) - Too Big
    'disclaimer.chaussures.grand.shoeTooBig.title':
      "Ma chaussure est trop grande, j'ai le pied qui bouge dans la chaussure !",
    'disclaimer.chaussures.grand.shoeTooBig.content':
      'Est-ce que la longueur est adaptée ? Si Oui vous pouvez ajouter une semelle pour stabiliser votre pied et gagner en confort.',

    // Return Disclaimer - CHAUSSURES (Shoes) - Too Small
    'disclaimer.chaussures.petit.shoeTooSmall.title': 'Ma chaussure est trop petite',
    'disclaimer.chaussures.petit.shoeTooSmall.content':
      'Si on ne peut pas mettre 1 doigts derrière le talon il faut prendre une taille plus grande.',

    // Schema titles (form step headers)
    'schema.clientStepTitle': 'Client',
    'schema.orderStepTitle': 'Commande',

    // Image alt text
    'img.returnHelp': 'aide retour commande',

    // Return reasons
    'reason.articleTooBig': 'Article trop grand',
    'reason.articleTooSmall': 'Article trop petit',
    'reason.colorNotSuitable': 'Coloris ne convient pas',
    'reason.resignation': 'Démission',
    'reason.qualityDefect': 'Défaut Qualité',
    'reason.duplicate': 'Doublon',
    'reason.bragardError': 'Erreur Bragard',
    'reason.clientError': 'Erreur Client',
    'reason.embroideryError': 'Erreur Broderie',
    'reason.sample': 'Echantillon',
    'reason.modelNotSuitable': 'Modèle ne convient pas',
    'reason.deliveryProblem': 'Problème livraison',
    'reason.commercialAgreement': 'Retour Accord commercial',
  },
  en: {
    // Menu labels
    'menu.returns': 'Returns',
    'menu.suivi': 'Tracking',
    'menu.orders': 'Orders',
    'menu.enterprises': 'Enterprises',
    'menu.iam': 'Access Management',
    'menu.users': 'Users',
    'menu.groups': 'Groups',
    'menu.policies': 'Policies',

    'menu.feedbacks': 'Feedback',
    'menu.feedbacks.description':
      'Report incorrect recommendations for superadmin review.',

    // Feedback
    'feedback.title': 'Feedback',
    'feedback.feedbackType': 'Feedback type',
    'feedback.type.sizes': 'Sizes',
    'feedback.listDescription': 'Feedback for the selected organization.',
    'feedback.formDescription':
      'Describe the enterprise, trousseau, person or morphology, and affected products.',
    'feedback.viewTitle': 'Feedback detail',
    'feedback.createTitle': 'New feedback',
    'feedback.editTitle': 'Edit feedback',
    'feedback.create': 'New feedback',
    'feedback.edit': 'Edit',
    'feedback.view': 'View',
    'feedback.save': 'Save',
    'feedback.formValidationError':
      'Check required fields (respondent, products, sizes).',
    'feedback.unknownField': 'Unknown field',
    'feedback.backToList': 'Back to list',
    'feedback.created': 'Feedback submitted',
    'feedback.updated': 'Feedback updated',
    'feedback.statusUpdated': 'Status updated',
    'feedback.filterStatus': 'Filter by status',
    'feedback.currentStatus': 'Current status',
    'feedback.createdAt': 'Created',
    'feedback.columnCreated': 'Created',
    'feedback.columnUpdated': 'Updated',
    'feedback.enterpriseId': 'Enterprise',
    'feedback.trousseauId': 'Trousseau',
    'feedback.personId': 'Respondent',
    'feedback.subjectMode': 'Identification',
    'feedback.subjectPerson': 'Respondent',
    'feedback.subjectMorphology': 'Morphology',
    'feedback.height': 'Height (cm)',
    'feedback.weight': 'Weight (kg)',
    'feedback.age': 'Age (years)',
    'feedback.neckGirth': 'Neck girth (cm)',
    'feedback.headGirth': 'Head girth (cm)',
    'feedback.chestGirth': 'Chest girth (cm)',
    'feedback.hipGirth': 'Hip girth (cm)',
    'feedback.products': 'Products',
    'feedback.productId': 'Product',
    'feedback.recommendedSize': 'Recommended size',
    'feedback.desiredSize': 'Desired size',
    'feedback.addProduct': 'Add product',
    'feedback.removeProduct': 'Remove product',
    'feedback.status': 'Status',
    'feedback.sortByDate': 'By date',
    'feedback.sortByName': 'By name',
    'feedback.feedbackId': 'Id',
    'feedback.enterpriseIdRequired': 'Enterprise is required',
    'feedback.trousseauIdRequired': 'Trousseau is required',
    'feedback.personIdRequired': 'Respondent is required',
    'feedback.heightRequired': 'Height is required',
    'feedback.weightRequired': 'Weight is required',
    'feedback.ageRequired': 'Age is required',
    'feedback.productIdRequired': 'Product is required',
    'feedback.recommendedSizeRequired': 'Recommended size is required',
    'feedback.desiredSizeRequired': 'Desired size is required',
    'feedback.status.deposited': 'Deposited',
    'feedback.status.in_progress': 'In progress',
    'feedback.status.processed': 'Processed',
    'feedback.tabSubject': 'Person / morphology',
    'feedback.tabSubjectDescription':
      'Provide either a respondent or morphology measurements (exactly one is required).',
    'feedback.productsFormDescription':
      'Add one row per product with recommended and desired sizes.',
    'feedback.scopeTitle': 'Target organization',
    'feedback.scopeDescription':
      'Feedback entries are stored in the selected organization database.',
    'feedback.processedAt': 'Processed at',
    'feedback.processedBy': 'Processed by',

    // Menu descriptions
    'menu.returns.description':
      'Request returns and manage return requests that have not been sent.',
    'menu.suivi.description': 'Track return requests and completed returns.',
    'menu.orders.description': 'View order history',
    'menu.enterprises.description': 'Manage enterprises on the platform',
    'menu.iam.description': 'Manage users, their groups and access policies',

    // Notifications
    'notification.return.error': 'Your return request has failed.',
    'notification.return.error.duplicate':
      'A return request already exists for this order.',
    'notification.return.success.title': 'Your return request has been sent.',
    'notification.return.success.description':
      'Thank you for your return. You will receive a confirmation email with the return instructions for your item(s).',
    'notification.return.pending.description':
      'Your request is being processed. Confirmation will update automatically.',
    'notification.return.pending.stillProcessing':
      'The return tracking is not confirmed yet. Check Suivi later.',

    // OrderNotFound
    'notFound.clientNotFound': 'Client not found',
    'notFound.orderNotFound': 'Order not found',
    'notFound.noResults': 'No results found',
    'notFound.noClientFound': 'No client found with number',
    'notFound.noOrderFound': 'No order found with number',
    'notFound.forClient': 'for client',
    'notFound.noResultsMessage': 'We did not find any results matching your search',
    'notFound.checkClientNumber': 'Check your client number',
    'notFound.clientNumberLocation':
      'The client number is at the top left of your invoice (e.g., 123456)',
    'notFound.checkOrderNumber': 'Check your order number',
    'notFound.orderNumberLocation':
      'The order number is in the "Related order" column of your invoice (8 digits)',
    'notFound.checkInvoice': 'Check your invoice',
    'notFound.invoiceInfo':
      'Have your invoice ready to easily find the required information',
    'notFound.searchedInfo': 'Searched information:',
    'notFound.client': 'Client:',
    'notFound.order': 'Order:',
    'notFound.newSearch': 'New search',
    'notFound.suggestions': 'Suggestions to find your information',

    // SearchOrder
    'searchOrder.clientNumber': 'Client number',
    'searchOrder.clientNumberHelp': 'Enter your client number (e.g., 123456 or 0123456)',
    'searchOrder.orderNumber': 'Order number',
    'searchOrder.orderNumberHelp': 'Enter your order number (8 digits)',
    'searchOrder.whereToFind': 'Where to find your numbers?',
    'searchOrder.clientNumberExample': 'E.g.:',
    'searchOrder.topLeftInvoice': 'In the “Customer No.” column of your invoice',
    'searchOrder.orderColumn': '"Related order" column',
    'searchOrder.understood': 'I understand',
    'searchOrder.searchTitle': 'Search your order',
    'searchOrder.searchSubtitle': 'Follow the steps to find your return information',
    'searchOrder.progress': 'Progress',
    'searchOrder.steps': 'steps',
    'searchOrder.step1Conditions': 'Step 1: Return conditions',
    'searchOrder.step2Client': 'Step 2: Client identification',
    'searchOrder.step3Order': 'Step 3: Order search',
    'searchOrder.conditionsIntro':
      'Before starting your return request, please read our conditions:',
    'searchOrder.itemState': 'Item condition',
    'searchOrder.itemStateContent':
      'Items returned new, in original packaging, unwashed, unaltered, unstained and unworn.',
    'searchOrder.returnDelay': 'Return period',
    'searchOrder.returnDelayContent':
      '30 working days from delivery. The shipping slip serves as proof.',
    'searchOrder.excludedProducts': 'Excluded products',
    'searchOrder.excludedProductsContent':
      'Personalized, embroidered, modified products and end-of-series items not accepted.',
    'searchOrder.refund': 'Refund',
    'searchOrder.refundContent': 'Be sure to include your bank details for any refund.',
    'searchOrder.acceptConditions': 'I have read and accept the conditions',
    'searchOrder.conditionsAccepted': 'Conditions accepted',
    'searchOrder.enterClientId': 'Enter your client number to access your orders.',
    'searchOrder.backToConditions': 'Back to conditions',
    'searchOrder.validateAndContinue': 'Validate and continue',
    'searchOrder.invalidClientFormat': 'Invalid client number format',
    'searchOrder.acceptConditionsFirst': 'Please accept the return conditions first',
    'searchOrder.modify': 'Edit',
    'searchOrder.enterOrderId':
      'Now enter your order number to access the details and return options.',
    'searchOrder.backToClient': 'Back to client identification',
    'searchOrder.searchOrder': 'Search order',
    'searchOrder.completeClientFirst': 'Please complete client identification first',
    'searchOrder.orderNumberToEnter': 'Order number to enter',

    // Button titles
    'button.returnItem': 'Return this item',
    'button.deleteReturn': 'Delete this return',
    'button.editReturn': 'Edit this return',
    'button.submitReturn': 'Submit this return',
    'button.validateReturn': 'Validate return',
    'button.myReturnRequests': 'My return requests',
    'button.submitAllReturns': 'Submit all returns',
    'button.back': 'Back',

    // Modal titles
    'modal.returnItem': 'Return an item',
    'modal.returnRequestsByOrder': 'Your return requests by order',

    // List titles
    'list.yourArticle': 'Your item',
    'list.yourArticles': 'Your items',
    'list.addItemsToReturn': 'Add items to return',
    'list.products': 'Products',
    'list.tracking': 'Tracking',

    // Order view
    'order.yourOrderNumber': 'Your order number',
    'order.of': 'from',
    'order.totalTtc': 'with a total incl. tax of',
    'order.status': 'status',
    'order.trackingExists': 'Tracking exists for this order',
    'order.viewTracking': 'View tracking',

    // Return conditions
    'conditions.title': 'Return conditions',
    'conditions.itemState.title': 'Item condition',
    'conditions.itemState.content':
      'Items must be returned complete and in new condition: in their original packaging, unwashed, unaltered, unstained and unworn.',
    'conditions.returnDelay.title': 'Return deadline',
    'conditions.returnDelay.content':
      'You have 30 business days from delivery to return your items. The shipping slip is proof. After this period, no returns will be accepted.',
    'conditions.excludedProducts.title': 'Excluded products',
    'conditions.excludedProducts.content':
      'Personalized, embroidered, modified and end-of-line products cannot be returned.',
    'conditions.refund.title': 'Refund',
    'conditions.refund.content':
      'For a refund, please include your bank details (RIB). Without bank details, your request cannot be processed.',

    // Return form
    'form.chooseQuantity': 'Choose the quantity to return',
    'form.quantityReturned': 'Quantity returned',
    'form.returnType': 'Return type',
    'form.returnReason': 'Return reason',
    'form.newSizeRequested': 'New size requested',
    'form.credit': 'Credit',
    'form.exchange': 'Exchange',
    'form.refund': 'Refund',
    'form.exchangeUnavailable': 'Exchange unavailable due to sizes being out of stock.',
    'form.ribWarning': 'Warning: please include your bank details with your return slip.',

    // Form errors
    'error.quantityRequired': 'Please enter a quantity to return',
    'error.returnTypeRequired': 'Please select a return type',
    'error.returnReasonRequired': 'Please choose a return reason',
    'error.quantityMin': 'Minimum quantity to return is 1 item',
    'error.sizeRequired': 'Please select a new size',

    // Misc
    'misc.itemNotReturnable': 'Item not returnable',
    'misc.loading': 'Loading...',
    'misc.client': 'Client',

    // Schema - Column titles
    'schema.orderNumber': 'Order number',
    'schema.clientReference': 'Client reference',
    'schema.totalAmount': 'Total amount',
    'schema.receptionDate': 'Reception date',
    'schema.status': 'Status',
    'schema.type': 'Type',
    'schema.lines': 'Lines',
    'schema.designation': 'Description',
    'schema.articleNumber': 'Article number',
    'schema.articleRef': 'Article ref',
    'schema.qtyOrdered': 'Qty ordered',
    'schema.qtyToReturn': 'Qty to return',
    'schema.qtyReturned': 'Qty returned',
    'schema.returnType': 'Return type',
    'schema.returnReason': 'Return reason',
    'schema.requestedSize': 'Requested size',
    'schema.replacementArticle': 'Replacement article code',
    'schema.unit': 'U',
    'schema.price': 'Price',
    'schema.total': 'Total',
    'schema.orderRef': 'Order',
    'schema.isReturnable': 'Returnable item?',
    'schema.schoolClient': 'School client',
    'schema.credit': 'Credit',
    'schema.exchange': 'Exchange',
    'schema.refund': 'Refund',
    'schema.productTracking': 'Product tracking',
    'schema.reference': 'Reference',
    'schema.replacedRef': 'Replaced ref',
    'schema.size': 'Size',
    'schema.clientReason': 'Client reason',
    'schema.tracking': 'Tracking',
    'schema.enterpriseId': 'Enterprise ID',
    'schema.trackingRef': 'Tracking ref',
    'schema.returnStatus': 'Return status',
    'schema.processingStatus': 'Processing status',
    'schema.trackingDate': 'Tracking date',
    'trace.returnStatus.pending': 'Request being processed',
    'trace.returnStatus.confirmed': 'Return registered',
    'trace.returnStatus.failed': 'Registration failed',
    'trace.processingStatus.pending': 'Awaiting sync',
    'trace.processingStatus.unavailable': 'Unavailable',
    'schema.name': 'Name',
    'schema.email': 'Email',
    'schema.creatorEmail': 'Creator email',
    'schema.creatorRole': 'Creator role',
    'schema.trackingUrl': 'Tracking URL',
    'schema.trackingType': 'Tracking type',
    'schema.returnMotif': 'Return reason',
    'schema.userId': 'User ID',
    'schema.createdBy': 'Created by',
    'schema.clientEmail': 'Client email',
    'schema.clientName': 'Client name',
    'schema.createdAt': 'Created at',
    'schema.orderId': 'Order ID',
    'schema.extOrderId': 'External ID',
    'schema.enterprise': 'Enterprise',
    'schema.orderDate': 'Order date',
    'schema.returnId': 'Return ID',
    'schema.eurekaStatus': 'Eureka status',
    'schema.error': 'Error',
    'schema.nameNotFound': 'Name not found',

    // Page titles
    'page.orders': 'Orders',
    'page.order': 'Order',
    'page.tracking': 'Tracking',

    // ReturnRequestTable
    'list.yourReturnRequest': 'Your return request',

    // AddReturnForm - schema labels
    'form.creditLabel': 'Credit',
    'form.exchangeLabel': 'Exchange',

    // ReturnDisclaimer modal
    'modal.attention': 'Warning!',
    'modal.optimizeChoice': 'Optimize your choice before returning your items',
    'button.cancel': 'Cancel',

    // SearchOrderModal
    'searchModal.searchOrder': 'Search order',
    'searchModal.searchAnOrder': 'Search an order',
    'searchModal.openDetails': 'Open details',
    'searchModal.search': 'Search',
    'searchModal.enterOrderNumber': 'Enter the order number:',
    'searchModal.orderPlaceholder': 'Order number Ex: 123456',
    'searchModal.searchReturn': 'Search return',
    'searchModal.searchAReturn': 'Search a return',
    'searchModal.enterOrderNumberForReturn':
      'Enter the order number to find the associated return:',
    'searchModal.multipleReturnsFound':
      '{{count}} returns found for this order. Select one to open.',
    'searchModal.returnNotFound': 'No return found for this order.',
    'searchModal.missingEnterprise': 'No enterprise selected — unable to run the search.',

    // Return Disclaimer - Return reasons
    'disclaimer.returnReason.tooBig': 'too big',
    'disclaimer.returnReason.tooSmall': 'too small',

    // Return Disclaimer - VESTE CUISIN (Chef Jacket) - Too Big
    'disclaimer.vesteCuisin.grand.sleeveLength.title': 'Sleeve length',
    'disclaimer.vesteCuisin.grand.sleeveLength.content':
      'You have the option to fold back (or roll up) the sleeves, the jackets are designed for this.',
    'disclaimer.vesteCuisin.grand.jacketTooLong.title': 'The jacket is too long',
    'disclaimer.vesteCuisin.grand.jacketTooLong.content':
      'If the jacket fits you at the shoulders, you should not change it, as it would be too small without freedom of movement.',
    'disclaimer.vesteCuisin.grand.jacketTooWide.title':
      'The jacket is too wide at the chest',
    'disclaimer.vesteCuisin.grand.jacketTooWide.content':
      'Check if it is a unisex jacket but the shoulders fit, meaning the size is correct: the jacket shape is straight and not fitted.',

    // Return Disclaimer - VESTE CUISIN (Chef Jacket) - Too Small
    'disclaimer.vesteCuisin.petit.sleeveLength.title': 'Sleeve length',
    'disclaimer.vesteCuisin.petit.sleeveLength.content':
      'If the shoulder size fits, you should not change it, as it would be too big and much less elegant!',
    'disclaimer.vesteCuisin.petit.jacketTooShort.title':
      'The jacket is too short in length',
    'disclaimer.vesteCuisin.petit.jacketTooShort.content':
      'If the jacket fits you at the shoulders, you should not change it, as it would be too big and much less elegant!',
    'disclaimer.vesteCuisin.petit.jacketTooTightHips.title':
      'The jacket is too tight at the hips (especially for women)',
    'disclaimer.vesteCuisin.petit.jacketTooTightHips.content':
      'If the shoulder size fits, the size is correct. The jacket shape is fitted to accentuate your feminine curves. If only the bottom button does not close, your apron will hide the open button and only the top of the jacket will be visible.',

    // Return Disclaimer - VESTE COSTU (Suit Jacket) - Too Big
    'disclaimer.vesteCostu.grand.sleeveLength.title': 'Sleeve length',
    'disclaimer.vesteCostu.grand.sleeveLength.content':
      'You have the option to fold back (or roll up) the sleeves, the jackets are designed for this.',
    'disclaimer.vesteCostu.grand.jacketTooLong.title': 'The jacket is too long',
    'disclaimer.vesteCostu.grand.jacketTooLong.content':
      'If the jacket fits you at the shoulders, you should not change it, as it would be too small without freedom of movement.',
    'disclaimer.vesteCostu.grand.jacketTooWide.title':
      'The jacket is too wide at the chest',
    'disclaimer.vesteCostu.grand.jacketTooWide.content':
      'Check if it is a unisex jacket but the shoulders fit, meaning the size is correct: the jacket shape is straight and not fitted.',

    // Return Disclaimer - VESTE COSTU (Suit Jacket) - Too Small
    'disclaimer.vesteCostu.petit.sleeveLength.title': 'Sleeve length',
    'disclaimer.vesteCostu.petit.sleeveLength.content':
      'If the shoulder size fits, you should not change it, as it would be too big and much less elegant!',
    'disclaimer.vesteCostu.petit.jacketTooShort.title':
      'The jacket is too short in length',
    'disclaimer.vesteCostu.petit.jacketTooShort.content':
      'If the jacket fits you at the shoulders, you should not change it, as it would be too big and much less elegant!',
    'disclaimer.vesteCostu.petit.jacketTooTightHips.title':
      'The jacket is too tight at the hips (especially for women)',
    'disclaimer.vesteCostu.petit.jacketTooTightHips.content':
      'If the shoulder size fits, the size is correct. The jacket shape is fitted to accentuate your feminine curves. If only the bottom button does not close, your apron will hide the open button and only the top of the jacket will be visible.',

    // Return Disclaimer - PANTALON (Pants) - Too Big
    'disclaimer.pantalon.grand.legLength.title': 'Leg length',
    'disclaimer.pantalon.grand.legLength.content':
      'Check for snaps at the bottom of the pants that allow you to adjust the length. If the pants do not have snaps, you can have a hem done by a tailor of your choice.',
    'disclaimer.pantalon.grand.legWidth.title': 'Leg width',
    'disclaimer.pantalon.grand.legWidth.content':
      'This is how the model is designed, changing the size will not change the cut of the pants.',
    'disclaimer.pantalon.grand.waistSize.title': 'Waist size',
    'disclaimer.pantalon.grand.waistSize.content':
      'If the pants have a drawstring, you can use it to adjust the size. If it is really too big at the waist "more than 3 fingers" after tightening, then the size is not suitable.',

    // Return Disclaimer - PANTALON (Pants) - Too Small
    'disclaimer.pantalon.petit.legLength.title': 'Leg length',
    'disclaimer.pantalon.petit.legLength.content':
      'Changing the size will not lengthen the pants legs. This is how the pants cut is designed.',
    'disclaimer.pantalon.petit.thighWidth.title': 'Thigh width but waist size is fine',
    'disclaimer.pantalon.petit.thighWidth.content':
      'Do not hesitate to take 1 to 2 sizes larger. You will need to adjust your pants with a belt.',
    'disclaimer.pantalon.petit.waistSize.title': 'Waist size',
    'disclaimer.pantalon.petit.waistSize.content': 'Take 1 to 2 additional sizes',

    // Return Disclaimer - CHEMISE (Shirt) - Too Big
    'disclaimer.chemise.grand.neckSize.title': 'Neck area',
    'disclaimer.chemise.grand.neckSize.content':
      'If you can fit 2 fingers at the collar, the size is suitable. A shirt too tight at the neck is very uncomfortable.',
    'disclaimer.chemise.grand.sleevesTooLong.title': 'Sleeves too long',
    'disclaimer.chemise.grand.sleevesTooLong.content':
      'If the neck fits, the size is suitable. This is how the shirt cut is designed.',
    'disclaimer.chemise.grand.shirtLength.title': 'Shirt length at the bottom',
    'disclaimer.chemise.grand.shirtLength.content':
      'If the neck fits, the size is suitable. This is how the shirt cut is designed.',

    // Return Disclaimer - CHEMISE (Shirt) - Too Small
    'disclaimer.chemise.petit.neckSize.title': 'Neck area',
    'disclaimer.chemise.petit.neckSize.content':
      'If you are too tight at the neck, take 1 to 2 larger sizes.',
    'disclaimer.chemise.petit.sleevesTooShort.title': 'Sleeves too short',
    'disclaimer.chemise.petit.sleevesTooShort.content':
      'If the neck fits, the shirt is suitable. This is how the shirt cut is designed.',
    'disclaimer.chemise.petit.shirtLength.title': 'Shirt length at the bottom',
    'disclaimer.chemise.petit.shirtLength.content':
      'If the neck fits, the shirt is suitable. This is how the shirt cut is designed.',

    // Return Disclaimer - CHAUSSURES (Shoes) - Too Big
    'disclaimer.chaussures.grand.shoeTooBig.title':
      'My shoe is too big, my foot moves around in the shoe!',
    'disclaimer.chaussures.grand.shoeTooBig.content':
      'Is the length suitable? If yes, you can add an insole to stabilize your foot and improve comfort.',

    // Return Disclaimer - CHAUSSURES (Shoes) - Too Small
    'disclaimer.chaussures.petit.shoeTooSmall.title': 'My shoe is too small',
    'disclaimer.chaussures.petit.shoeTooSmall.content':
      'If you cannot fit 1 finger behind the heel, you need to take a larger size.',

    // Schema titles (form step headers)
    'schema.clientStepTitle': 'Client',
    'schema.orderStepTitle': 'Order',

    // Image alt text
    'img.returnHelp': 'return order help',

    // Return reasons
    'reason.articleTooBig': 'Article too big',
    'reason.articleTooSmall': 'Article too small',
    'reason.colorNotSuitable': 'Color not suitable',
    'reason.resignation': 'Resignation',
    'reason.qualityDefect': 'Quality defect',
    'reason.duplicate': 'Duplicate',
    'reason.bragardError': 'Bragard error',
    'reason.clientError': 'Client error',
    'reason.embroideryError': 'Embroidery error',
    'reason.sample': 'Sample',
    'reason.modelNotSuitable': 'Model not suitable',
    'reason.deliveryProblem': 'Delivery problem',
    'reason.commercialAgreement': 'Commercial agreement return',
  },
};

/** Translation keys type */
export type TranslationKey = keyof (typeof translations)['fr'];

/**
 * Get current language (respects user override from Avatar menu)
 * @deprecated Use getCurrentLanguage from @fastlessjs/backoffice-layer instead
 */
export const getBrowserLanguage = (): Language => {
  return getCurrentLanguage();
};

/**
 * Translate a key to current language (respects user preference)
 */
export const t = (key: TranslationKey): string => {
  const lang: Language = getCurrentLanguage();
  const langTranslations = translations[lang];
  return langTranslations[key] || translations.fr[key] || key;
};

/**
 * Get translation with fallback
 */
export const translate = (key: string, fallback?: string): string => {
  const lang: Language = getCurrentLanguage();
  const langTranslations = translations[lang];
  const translation = langTranslations[key as TranslationKey];
  return translation || fallback || key;
};

/**
 * Register translations with the layer
 * This is called when the module is imported (in main.tsx)
 */
registerBreadcrumbTranslations(breadcrumbTranslations);
registerHomeTranslations(homeTranslations);
